/**
 * @engine/vfx — VFX preset builders (single-system group).
 *
 * Pure factories returning VFXGraphData, extracted from VFXGraphAsset.ts.
 * Re-exported from VFXGraphAsset so existing import paths are unchanged.
 */

import type { VFXGraphData } from '../types';
import { regenerateGraphIds } from './_shared';

/**
 * Create a firework VFX graph with preset nodes.
 *
 * Includes:
 * - Burst spawn (50-100 particles)
 * - Random sphere position with small radius
 * - Cone velocity (upward spread)
 * - Gravity and drag
 * - Color over life (orange -> red -> fade)
 * - Size over life (shrink)
 * - Quad output
 */

export function createPhoenixRisingVFX(
  name: string = "Phoenix Rising",
): VFXGraphData {
  const template: VFXGraphData = {
    id: "steam_phoenix_rising",
    name: "Phoenix Rising",
    nodes: [
      // Fire system
      {
        id: "spawn_fire",
        type: "vfx.spawn.rate",
        position: { x: 50, y: 50 },
        inputValues: { rate: 500 },
        contextId: "ctx_spawn_fire",
      },
      {
        id: "init_fire_lifetime",
        type: "vfx.init.lifetime_random",
        position: { x: 50, y: 50 },
        inputValues: { min: 1.5, max: 3 },
        contextId: "ctx_init_fire",
      },
      {
        id: "init_fire_position",
        type: "vfx.init.position_shape",
        position: { x: 50, y: 150 },
        inputValues: {
          shape: "sphere",
          radius: 1,
          radiusVariance: 0.3,
          center: { x: 0, y: -2, z: 0 },
        },
        contextId: "ctx_init_fire",
      },
      {
        id: "init_fire_velocity",
        type: "vfx.init.velocity_direction",
        position: { x: 50, y: 250 },
        inputValues: { direction: { x: 0, y: 1, z: 0 }, speed: 5, spread: 35 },
        contextId: "ctx_init_fire",
      },
      {
        id: "init_fire_size",
        type: "vfx.init.size_random",
        position: { x: 50, y: 350 },
        inputValues: { min: 0.4, max: 1.2 },
        contextId: "ctx_init_fire",
      },
      {
        id: "init_fire_color",
        type: "vfx.init.color_random",
        position: { x: 50, y: 450 },
        inputValues: {
          gradient: {
            stops: [
              { t: 0, color: { r: 1, g: 0.9, b: 0.3, a: 1 } },
              { t: 0.3, color: { r: 1, g: 0.6, b: 0.1, a: 1 } },
              { t: 0.7, color: { r: 1, g: 0.3, b: 0.05, a: 1 } },
              { t: 1, color: { r: 0.8, g: 0.1, b: 0.02, a: 1 } },
            ],
          },
        },
        contextId: "ctx_init_fire",
      },
      {
        id: "init_fire_angular",
        type: "vfx.init.angular_velocity",
        position: { x: 50, y: 550 },
        inputValues: { speed: 0, variance: 180 },
        contextId: "ctx_init_fire",
      },
      {
        id: "update_fire_turbulence",
        type: "vfx.update.turbulence",
        position: { x: 50, y: 50 },
        inputValues: {
          intensity: 3,
          frequency: 1.2,
          octaves: 4,
          roughness: 0.5,
        },
        contextId: "ctx_update_fire",
      },
      {
        id: "update_fire_drag",
        type: "vfx.update.drag",
        position: { x: 50, y: 150 },
        inputValues: { coefficient: 0.8 },
        contextId: "ctx_update_fire",
      },
      {
        id: "update_fire_rotation",
        type: "vfx.update.rotation",
        position: { x: 50, y: 250 },
        inputValues: {},
        contextId: "ctx_update_fire",
      },
      {
        id: "update_fire_color",
        type: "vfx.update.color_over_life",
        position: { x: 50, y: 350 },
        inputValues: {
          gradient: {
            stops: [
              { t: 0, color: { r: 1, g: 0.95, b: 0.6, a: 1 } },
              { t: 0.2, color: { r: 1, g: 0.7, b: 0.2, a: 1 } },
              { t: 0.5, color: { r: 1, g: 0.4, b: 0.1, a: 0.9 } },
              { t: 0.8, color: { r: 0.8, g: 0.15, b: 0.05, a: 0.6 } },
              { t: 1, color: { r: 0.3, g: 0.05, b: 0.02, a: 0 } },
            ],
          },
        },
        contextId: "ctx_update_fire",
      },
      {
        id: "update_fire_size",
        type: "vfx.update.size_over_life",
        position: { x: 50, y: 450 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 0.3 },
              { t: 0.15, v: 1 },
              { t: 0.5, v: 0.8 },
              { t: 0.85, v: 0.4 },
              { t: 1, v: 0 },
            ],
          },
          scale: 1.5,
        },
        contextId: "ctx_update_fire",
      },
      {
        id: "output_fire",
        type: "vfx.output.quad",
        position: { x: 50, y: 50 },
        inputValues: { blendMode: 1 },
        contextId: "ctx_output_fire",
      },
      // Embers system
      {
        id: "spawn_embers",
        type: "vfx.spawn.rate",
        position: { x: 50, y: 50 },
        inputValues: { rate: 200 },
        contextId: "ctx_spawn_embers",
      },
      {
        id: "init_embers_lifetime",
        type: "vfx.init.lifetime_random",
        position: { x: 50, y: 50 },
        inputValues: { min: 2, max: 5 },
        contextId: "ctx_init_embers",
      },
      {
        id: "init_embers_position",
        type: "vfx.init.position_shape",
        position: { x: 50, y: 150 },
        inputValues: {
          shape: "sphere",
          radius: 1.5,
          radiusVariance: 0.5,
          center: { x: 0, y: 0, z: 0 },
        },
        contextId: "ctx_init_embers",
      },
      {
        id: "init_embers_velocity",
        type: "vfx.init.velocity_direction",
        position: { x: 50, y: 250 },
        inputValues: { direction: { x: 0, y: 1, z: 0 }, speed: 3, spread: 60 },
        contextId: "ctx_init_embers",
      },
      {
        id: "init_embers_size",
        type: "vfx.init.size_random",
        position: { x: 50, y: 350 },
        inputValues: { min: 0.03, max: 0.1 },
        contextId: "ctx_init_embers",
      },
      {
        id: "init_embers_color",
        type: "vfx.init.color_random",
        position: { x: 50, y: 450 },
        inputValues: {
          gradient: {
            stops: [
              { t: 0, color: { r: 1, g: 0.8, b: 0.3, a: 1 } },
              { t: 0.5, color: { r: 1, g: 0.5, b: 0.1, a: 1 } },
              { t: 1, color: { r: 1, g: 0.3, b: 0.05, a: 1 } },
            ],
          },
        },
        contextId: "ctx_init_embers",
      },
      {
        id: "update_embers_gravity",
        type: "vfx.update.gravity",
        position: { x: 50, y: 50 },
        inputValues: { force: { x: 0, y: 2, z: 0 } },
        contextId: "ctx_update_embers",
      },
      {
        id: "update_embers_turbulence",
        type: "vfx.update.turbulence",
        position: { x: 50, y: 150 },
        inputValues: { intensity: 2, frequency: 1, octaves: 2, roughness: 0.6 },
        contextId: "ctx_update_embers",
      },
      {
        id: "update_embers_drag",
        type: "vfx.update.drag",
        position: { x: 50, y: 250 },
        inputValues: { coefficient: 0.5 },
        contextId: "ctx_update_embers",
      },
      {
        id: "update_embers_alpha",
        type: "vfx.update.alpha_over_life",
        position: { x: 50, y: 350 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 0 },
              { t: 0.1, v: 1 },
              { t: 0.7, v: 0.8 },
              { t: 1, v: 0 },
            ],
          },
        },
        contextId: "ctx_update_embers",
      },
      {
        id: "output_embers",
        type: "vfx.output.quad",
        position: { x: 50, y: 50 },
        inputValues: { blendMode: 1 },
        contextId: "ctx_output_embers",
      },
      // Wings system
      {
        id: "spawn_wings",
        type: "vfx.spawn.rate",
        position: { x: 50, y: 50 },
        inputValues: { rate: 100 },
        contextId: "ctx_spawn_wings",
      },
      {
        id: "init_wings_lifetime",
        type: "vfx.init.lifetime_random",
        position: { x: 50, y: 50 },
        inputValues: { min: 0.8, max: 1.5 },
        contextId: "ctx_init_wings",
      },
      {
        id: "init_wings_position",
        type: "vfx.init.position_line",
        position: { x: 50, y: 150 },
        inputValues: {
          start: { x: -2, y: 0, z: 0 },
          end: { x: 2, y: 0, z: 0 },
        },
        contextId: "ctx_init_wings",
      },
      {
        id: "init_wings_velocity",
        type: "vfx.init.velocity_direction",
        position: { x: 50, y: 250 },
        inputValues: {
          direction: { x: 0, y: -0.3, z: 0 },
          speed: 2,
          spread: 30,
        },
        contextId: "ctx_init_wings",
      },
      {
        id: "init_wings_size",
        type: "vfx.init.size_random",
        position: { x: 50, y: 350 },
        inputValues: { min: 0.2, max: 0.5 },
        contextId: "ctx_init_wings",
      },
      {
        id: "init_wings_color",
        type: "vfx.init.color",
        position: { x: 50, y: 450 },
        inputValues: { color: { r: 1, g: 0.7, b: 0.2, a: 1 } },
        contextId: "ctx_init_wings",
      },
      {
        id: "update_wings_gravity",
        type: "vfx.update.gravity",
        position: { x: 50, y: 50 },
        inputValues: { force: { x: 0, y: -1, z: 0 } },
        contextId: "ctx_update_wings",
      },
      {
        id: "update_wings_alpha",
        type: "vfx.update.alpha_over_life",
        position: { x: 50, y: 150 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 0.8 },
              { t: 0.3, v: 1 },
              { t: 0.7, v: 0.6 },
              { t: 1, v: 0 },
            ],
          },
        },
        contextId: "ctx_update_wings",
      },
      {
        id: "update_wings_size",
        type: "vfx.update.size_over_life",
        position: { x: 50, y: 250 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 0.5 },
              { t: 0.2, v: 1 },
              { t: 0.8, v: 0.6 },
              { t: 1, v: 0 },
            ],
          },
          scale: 1,
        },
        contextId: "ctx_update_wings",
      },
      {
        id: "output_wings",
        type: "vfx.output.quad",
        position: { x: 50, y: 50 },
        inputValues: { blendMode: 1 },
        contextId: "ctx_output_wings",
      },
    ],
    edges: [],
    variables: [],
    contexts: [
      {
        id: "ctx_spawn_fire",
        type: "vfx.spawn",
        position: { x: 50, y: 50 },
        size: { width: 280, height: 120 },
        nodeIds: ["spawn_fire"],
        collapsed: false,
      },
      {
        id: "ctx_init_fire",
        type: "vfx.initialize",
        position: { x: 50, y: 200 },
        size: { width: 280, height: 600 },
        nodeIds: [
          "init_fire_lifetime",
          "init_fire_position",
          "init_fire_velocity",
          "init_fire_size",
          "init_fire_color",
          "init_fire_angular",
        ],
        collapsed: false,
      },
      {
        id: "ctx_update_fire",
        type: "vfx.update",
        position: { x: 50, y: 850 },
        size: { width: 280, height: 500 },
        nodeIds: [
          "update_fire_turbulence",
          "update_fire_drag",
          "update_fire_rotation",
          "update_fire_color",
          "update_fire_size",
        ],
        collapsed: false,
      },
      {
        id: "ctx_output_fire",
        type: "vfx.output",
        position: { x: 50, y: 1400 },
        size: { width: 280, height: 120 },
        nodeIds: ["output_fire"],
        collapsed: false,
      },
      {
        id: "ctx_spawn_embers",
        type: "vfx.spawn",
        position: { x: 400, y: 50 },
        size: { width: 280, height: 120 },
        nodeIds: ["spawn_embers"],
        collapsed: false,
      },
      {
        id: "ctx_init_embers",
        type: "vfx.initialize",
        position: { x: 400, y: 200 },
        size: { width: 280, height: 500 },
        nodeIds: [
          "init_embers_lifetime",
          "init_embers_position",
          "init_embers_velocity",
          "init_embers_size",
          "init_embers_color",
        ],
        collapsed: false,
      },
      {
        id: "ctx_update_embers",
        type: "vfx.update",
        position: { x: 400, y: 750 },
        size: { width: 280, height: 400 },
        nodeIds: [
          "update_embers_gravity",
          "update_embers_turbulence",
          "update_embers_drag",
          "update_embers_alpha",
        ],
        collapsed: false,
      },
      {
        id: "ctx_output_embers",
        type: "vfx.output",
        position: { x: 400, y: 1200 },
        size: { width: 280, height: 120 },
        nodeIds: ["output_embers"],
        collapsed: false,
      },
      {
        id: "ctx_spawn_wings",
        type: "vfx.spawn",
        position: { x: 750, y: 50 },
        size: { width: 280, height: 120 },
        nodeIds: ["spawn_wings"],
        collapsed: false,
      },
      {
        id: "ctx_init_wings",
        type: "vfx.initialize",
        position: { x: 750, y: 200 },
        size: { width: 280, height: 500 },
        nodeIds: [
          "init_wings_lifetime",
          "init_wings_position",
          "init_wings_velocity",
          "init_wings_size",
          "init_wings_color",
        ],
        collapsed: false,
      },
      {
        id: "ctx_update_wings",
        type: "vfx.update",
        position: { x: 750, y: 750 },
        size: { width: 280, height: 300 },
        nodeIds: [
          "update_wings_gravity",
          "update_wings_alpha",
          "update_wings_size",
        ],
        collapsed: false,
      },
      {
        id: "ctx_output_wings",
        type: "vfx.output",
        position: { x: 750, y: 1100 },
        size: { width: 280, height: 120 },
        nodeIds: ["output_wings"],
        collapsed: false,
      },
    ],
    contextFlowEdges: [
      {
        id: "flow_fire_1",
        fromContextId: "ctx_spawn_fire",
        fromPortId: "spawn_event",
        toContextId: "ctx_init_fire",
        toPortId: "spawn_event",
      },
      {
        id: "flow_fire_2",
        fromContextId: "ctx_init_fire",
        fromPortId: "particle",
        toContextId: "ctx_update_fire",
        toPortId: "particle",
      },
      {
        id: "flow_fire_3",
        fromContextId: "ctx_update_fire",
        fromPortId: "particle",
        toContextId: "ctx_output_fire",
        toPortId: "particle",
      },
      {
        id: "flow_embers_1",
        fromContextId: "ctx_spawn_embers",
        fromPortId: "spawn_event",
        toContextId: "ctx_init_embers",
        toPortId: "spawn_event",
      },
      {
        id: "flow_embers_2",
        fromContextId: "ctx_init_embers",
        fromPortId: "particle",
        toContextId: "ctx_update_embers",
        toPortId: "particle",
      },
      {
        id: "flow_embers_3",
        fromContextId: "ctx_update_embers",
        fromPortId: "particle",
        toContextId: "ctx_output_embers",
        toPortId: "particle",
      },
      {
        id: "flow_wings_1",
        fromContextId: "ctx_spawn_wings",
        fromPortId: "spawn_event",
        toContextId: "ctx_init_wings",
        toPortId: "spawn_event",
      },
      {
        id: "flow_wings_2",
        fromContextId: "ctx_init_wings",
        fromPortId: "particle",
        toContextId: "ctx_update_wings",
        toPortId: "particle",
      },
      {
        id: "flow_wings_3",
        fromContextId: "ctx_update_wings",
        fromPortId: "particle",
        toContextId: "ctx_output_wings",
        toPortId: "particle",
      },
    ],
    contextSettings: {
      ctx_init_fire: { capacity: 3000, boundsMode: "auto" },
      ctx_init_embers: { capacity: 2000, boundsMode: "auto" },
      ctx_init_wings: { capacity: 500, boundsMode: "auto" },
    },
    capacity: { maxParticles: 6000, boundsMode: "auto" },
    simulation: { space: "world", deltaTimeMode: "deltaTime" },
    rendering: { sortMode: "none", blendMode: "additive" },
  };
  return regenerateGraphIds(template, name);
}

/**
 * Water Ripple Grid - 100x100 particle grid with ripple waves and wobble.
 *
 * Features:
 * - 10000 particles arranged in a grid (100x100)
 * - Grid position calculated via operators (Get Particle Index → Modulo/Floor → Compose Vec3)
 * - Cell spacing of 3 units
 * - Small noise-based wobble/jitter
 * - Multiple ripple wave sources
 * - Blue HDR gradient with intensity boost
 * - Infinite lifetime (particles stay in place)
 */
